/**
 * PurposeCard — "Purpose" section of the sandbox details fly-up. Shows the
 * current purpose badge plus user-editable `reason` + `linkedUrl` fields wired
 * to the PATCH /annotations endpoint via `useUpdateInstanceAnnotations`.
 * Rendered as a flat SettingsSection (borderless under the cofactor brand).
 */
import React from 'react';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { Button, SettingsSection, Text, TextArea, TextField } from '@chatsdet/ui';
import type { EnvironmentInstance } from '../../../../hooks/useEnvironmentInstances';
import { useUpdateInstanceAnnotations } from '../../../../hooks/useEnvironmentInstances';
import { PurposeBadge } from '../PurposeBadge';

export interface PurposeCardProps {
  instance: EnvironmentInstance;
}

export function PurposeCard({ instance }: PurposeCardProps) {
  const [editing, setEditing] = React.useState(false);
  const [reason, setReason] = React.useState(instance.reason ?? '');
  const [linkedUrl, setLinkedUrl] = React.useState(instance.linkedUrl ?? '');
  const mutation = useUpdateInstanceAnnotations(instance.id);

  // Re-sync local form state with server data whenever the user isn't mid-edit.
  React.useEffect(() => {
    if (!editing) {
      setReason(instance.reason ?? '');
      setLinkedUrl(instance.linkedUrl ?? '');
    }
  }, [editing, instance.reason, instance.linkedUrl]);

  const handleSave = async () => {
    try {
      await mutation.mutateAsync({
        reason: reason.trim() || null,
        linkedUrl: linkedUrl.trim() || null,
      });
      setEditing(false);
    } catch {
      // mutation.error drives the inline error message; stay in edit mode.
    }
  };

  const linkedUrlInvalid =
    linkedUrl.trim().length > 0 && !linkedUrl.trim().startsWith('https://');

  return (
    <SettingsSection
      title="Purpose"
      action={
        <Button
          variant="ghost"
          size="sm"
          onPress={() => setEditing((v) => !v)}
          isDisabled={mutation.isPending}
        >
          {editing ? 'Cancel' : 'Edit'}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div>
          <PurposeBadge purpose={instance.purpose} size="lg" />
        </div>
        {editing ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <TextArea
                label="Reason"
                value={reason}
                onChange={setReason}
                rows={3}
                maxLength={500}
                placeholder="What is this sandbox for?"
              />
              <span className="self-end text-xs text-muted-foreground tabular-nums">
                {reason.length}/500
              </span>
            </div>
            <TextField
              label="Linked URL"
              value={linkedUrl}
              onChange={setLinkedUrl}
              placeholder="https://github.com/org/repo/pull/123"
              description="Must be https://; rendered as a clickable chip."
              isInvalid={linkedUrlInvalid}
            />
            {mutation.error ? (
              <Text size="sm" className="text-red-600">
                {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
              </Text>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onPress={() => setEditing(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onPress={handleSave}
                isDisabled={mutation.isPending || linkedUrlInvalid}
              >
                {mutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {instance.reason ? (
              <Text className="text-[15px] leading-relaxed">{instance.reason}</Text>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="border border-dashed border-border rounded-md px-3 py-2 text-sm text-muted-foreground hover:border-primary hover:text-foreground transition-colors text-left"
              >
                + Add a reason for this sandbox
              </button>
            )}
            {instance.linkedUrl ? (
              <a
                href={instance.linkedUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="self-start inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-50 border border-border text-[12.5px] font-mono text-primary hover:underline max-w-full truncate"
              >
                <ArrowSquareOut size={12} />
                <span className="truncate">{instance.linkedUrl}</span>
              </a>
            ) : null}
          </div>
        )}
      </div>
    </SettingsSection>
  );
}

PurposeCard.displayName = 'PurposeCard';
